import { AlertCircle, ClipboardCheck } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import type { SubmitHandler, UseFormReturn } from "react-hook-form";
import { AppPageLayout } from "@/components/common/AppPageLayout";
import { commonComponents as cc } from "@/components/common";
import { FormDisplayProvider, LabelInputScope, RichLabel } from "@patient-watch/forms";
import { ui } from "@patient-watch/ui";
import type { BlockT, FormViewT, InputT, PageViewT } from "@patient-watch/types";
import type { AnyRoute } from "@tanstack/react-router";
import { findImageCountsRule, parsePageValidation } from "@patient-watch/forms/pageValidation";
import type { DueEntryT } from "@patient-watch/types";
import { cn } from "@/lib/utils";
import { services as s } from "@/services";
import { useSelectFormById } from "../../forms/api/selectFormById";
import InputSwitch from "../../inputs/components/InputSwitch";
import { BlockSwitch } from "@/features/blocks/components/BlockSwitch";
import FormProgress from "./FormProgress";
import CheckboxGroupCard from "./CheckboxGroupCard";
import PageGroupTotal from "./PageGroupTotal";
import ImageCountsGroup from "./ImageCountsGroup";
import { pairImageCounts } from "../utils/imageCounts";
import { useInputVisibility } from "../hooks/useInputVisibility";
import { useCarriedForm } from "../hooks/useCarriedForm";
import type { CarriedInputT } from "../utils/markCarriedInputs";
import { restrictChoices } from "../utils/restrictChoices";
import { useOwnOptions } from "@/features/dictionaries/api/selectOwnOptions";
import { groupCheckboxRuns } from "../utils/groupCheckboxRuns";
import { useExclusiveCheckboxes } from "../hooks/useExclusiveCheckboxes";
import { useExclusiveOptions } from "../hooks/useExclusiveOptions";
import EntryForScheduleLine from "./EntryForScheduleLine";
import InlineLateralityLine from "./InlineLateralityLine";
import { shouldShowLateralityLine } from "../utils/shouldShowLateralityLine";
import { entryScheduleLabelFromNextEntry } from "../utils/entryScheduleLabel";
import { useFormPageCopy } from "@/features/i18n/formPageCopy";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useMatches, useNavigate, useParams, useRouter, useSearch } from "@tanstack/react-router";
import { goBackOrFallback } from "@/features/navigation/goBackOrFallback";
import { hasPreviousFormPage as formHasPreviousPage } from "@/features/pages/utils/hasPreviousFormPage";
import { focusFirstFormInput } from "@/features/pages/utils/focusFirstFormInput";
import { firstFormPageIndex, shouldShowFormIntro } from "@/features/pages/utils/formIntro";
import { pageLabelRepeatsQuestion } from "@/features/pages/utils/pageLabelRepeatsQuestion";
import {
    formDisplayOptions,
    formHasPublicCover,
    formInstructionsPlacement,
    formTitleAlign,
    trimmedField,
} from "@/features/pages/utils/formChrome";
import { useFormIntroStore } from "@/features/pages/hooks/useFormIntroStore";
import { useEntryValuesStore } from "@/features/inputs/hooks/useEntryValuesStore";
import FormIntroScreen from "./FormIntroScreen";
import { FormInstrumentTitle } from "./FormInstrumentTitle";
import { publicFormColumnHeightClass } from "@/components/common/appBottomClearance";
import {
    formPageColumnClass,
    formPageFooterActionsClass,
    formPageFooterFrameClass,
    formPageShellFillClass,
} from "./formPageLayout";

interface Props {
    route: AnyRoute;
    pageIndex: number;
    form: FormViewT;
    nextEntry?: DueEntryT | null;
    diaryId?: string;
    /** Left-aligned line above the page title on pages with questions when the tracker measures a specific side. */
    lateralityBanner?: string | null;
    formType: "tracker";
    // Form control props
    reactHookForm: UseFormReturn<Record<string, unknown>>;
    onSubmit: SubmitHandler<Record<string, unknown>>;
    prevPage: () => void;
    nextPage?: () => void;
    isSubmitting?: boolean;
    isLastPage: boolean;
    visitedPageCount?: number;
    progressPageIndex?: number;
    progressPageTotal?: number;
    msAdlSelectionInputId?: string;
    /** Which form page row to render (defaults to pageIndex). */
    renderPageIndex?: number;
    /** Fill a fixed frame, such as the design phone, instead of the viewport. */
    constrain?: boolean;
    /** Render `form` exactly as given instead of refetching the saved form, as the form builder preview does. */
    formAsGiven?: boolean;
    /** Hide Back when it would leave the form, as on a screener, which opens straight on its first page. */
    hideBackOnFirstPage?: boolean;
}

// Define an interface for inputs with input_index
// We're using a type assertion approach instead of extending InputT
type InputWithIndex = InputT & {
    input_index?: number;
};

type BlockWithIndex = BlockT & {
    block_index?: number;
};

type PageRenderableItem =
    | { kind: "input"; index: number; key: string; input: InputT }
    | { kind: "block"; index: number; key: string; block: BlockT };

export type AnyInputT = string | string[] | number;

const FormPage = (props: Props) => {
    const router = useRouter();
    const matches = useMatches();
    const navigate = useNavigate();
    const copy = useFormPageCopy();
    const isEditingAnswers = (useSearch({ strict: false }) as { edit?: boolean }).edit === true;
    const { locale } = useAppLocale();
    const { data: fetchedForm } = useSelectFormById(props.formAsGiven ? "" : props.form.form_id, locale);
    const { entryId: routeEntryId } = useParams({ strict: false });
    const form = useCarriedForm(fetchedForm, props.diaryId, routeEntryId);
    const isAppRoute = matches.some((match) => match.routeId === "/_app");

    const resolvedForm = form ?? props.form;

    const pageIndexForRender = props.renderPageIndex ?? props.pageIndex;
    const page = resolvedForm?.pages?.find((p: PageViewT) => p?.page_index === pageIndexForRender) as PageViewT;
    const errorCount = Object.keys(props.reactHookForm.formState.errors).length;
    const submitCount = props.reactHookForm.formState.submitCount;
    const prevSubmitCountRef = useRef(submitCount);
    const fieldsRef = useRef<HTMLDivElement>(null);

    // Instructions before start: the intro is the first screen of page 1, not a separate route.
    const { entryId } = useParams({ strict: false });
    const introKey = entryId ?? resolvedForm?.form_id ?? "";
    const instructionPlacement = formInstructionsPlacement(resolvedForm);
    const usesIntroScreen = instructionPlacement === "intro";
    const introStarted = useFormIntroStore((state) => state.started[introKey]);
    const setIntroStarted = useFormIntroStore((state) => state.setStarted);
    useExclusiveCheckboxes(props.reactHookForm, page?.page_validation);
    useExclusiveOptions(props.reactHookForm, page?.page_validation);
    const visibility = useInputVisibility(
        props.reactHookForm.control,
        Array.isArray(page?.inputs) ? (page.inputs as InputT[]) : []
    );
    // Own options picked on earlier pages that follow-on questions (options_from with own: true) offer.
    const ownOptionIds = useMemo(
        () => [...new Set([...visibility.allowedOwn.values()].flat())],
        [visibility.allowedOwn]
    );
    const ownOptions = useOwnOptions(ownOptionIds);
    const hasStoredAnswers = useEntryValuesStore((state) =>
        entryId ? state.entry_values.some((value) => value.entry_id === entryId) : false
    );
    const isFirstPage = props.pageIndex === firstFormPageIndex(resolvedForm?.pages);
    const showIntro = shouldShowFormIntro({
        usesIntroScreen,
        isFirstPage,
        started: introStarted,
        hasStoredAnswers,
    });

    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            s.scroll.smoothScrollToTop();
            if (showIntro || props.constrain || !fieldsRef.current) return;
            focusFirstFormInput(fieldsRef.current);
        });
        return () => cancelAnimationFrame(frame);
    }, [props.pageIndex, pageIndexForRender, showIntro, props.constrain]);

    // Auto-scroll to top only when a new submission attempt fails with errors
    useEffect(() => {
        // Only scroll if submitCount increased (new submission attempt) and there are errors
        if (submitCount > prevSubmitCountRef.current && errorCount > 0) {
            s.scroll.smoothScrollToTop();
        }
        prevSubmitCountRef.current = submitCount;
    }, [submitCount, errorCount]);

    if (form?.pages && Number(props.pageIndex) > form.pages.length) {
        return (
            <div className="flex flex-col gap-4 bg-green-200 mx-auto p-2 px-8 py-4 rounded-lg w-fit text-green-800">
                <div className="flex items-center gap-4 mx-auto">
                    <ClipboardCheck size={48} strokeWidth={1.5} />
                    <p className="font-medium text-lg">{copy.formSubmitted}</p>
                </div>

                <fc.NextButton variant="outline" label={copy.next} className="w-auto" />
            </div>
        );
    }

    // Intro-screen forms leave the instrument title off later pages. The intro itself is instructions only.
    const hideInstrumentTitle = usesIntroScreen && !showIntro;
    const caption = hideInstrumentTitle ? null : trimmedField(resolvedForm?.form_caption);
    const instrumentName = hideInstrumentTitle ? null : trimmedField(resolvedForm?.form_name);
    const kicker = hideInstrumentTitle ? null : trimmedField(resolvedForm?.form_kicker);
    const hasInstrumentTitle = Boolean(caption || instrumentName || kicker);
    const display = formDisplayOptions(resolvedForm);
    const pageLabel = trimmedField(page?.page_label);
    const pageDescription = trimmedField(page?.page_description);
    const pageInputsForLabel = Array.isArray(page?.inputs) ? (page.inputs as InputT[]) : [];
    const showPageLabel = Boolean(
        pageLabel &&
        !pageLabelRepeatsQuestion(
            pageLabel,
            pageInputsForLabel.map((input) => input.input_label)
        )
    );
    // Only pages with questions name the side; instructions, thank-you and ready-to-save pages do not.
    const laterality =
        props.lateralityBanner && shouldShowLateralityLine(props.lateralityBanner, pageInputsForLabel.length) ? (
            <InlineLateralityLine text={props.lateralityBanner} />
        ) : null;
    const heading = (
        <FormInstrumentTitle
            className="mb-1 shrink-0"
            variant="page"
            align={formTitleAlign(resolvedForm)}
            caption={caption}
            name={instrumentName}
            kicker={kicker}
        />
    );

    const scheduleLabel = entryScheduleLabelFromNextEntry(props.nextEntry, locale);
    const isLastPage = props.isLastPage;
    const canGoToPreviousFormPage = formHasPreviousPage(props.visitedPageCount, props.pageIndex);
    const isFirstFormPage = isFirstPage;
    const backLeavesForm = !canGoToPreviousFormPage && !(usesIntroScreen && isFirstFormPage && !showIntro);
    const showBack = !(props.hideBackOnFirstPage && backLeavesForm);
    const handleBack = () => {
        if (usesIntroScreen && isFirstFormPage && !showIntro) {
            setIntroStarted(introKey, false);
            return;
        }
        if (canGoToPreviousFormPage) {
            props.prevPage();
            return;
        }
        const formId = resolvedForm?.form_id;
        const pathname = router.state.location.pathname;
        const validationForm = pathname.match(/^\/validate\/([^/]+)\/forms\/([^/]+)/);
        if (validationForm && formId) {
            void navigate({
                to: "/validate/$validationId/forms/$formId",
                params: { validationId: validationForm[1], formId },
            });
            return;
        }
        const onPublicForm = pathname.startsWith("/forms/");
        if (onPublicForm && formId && formHasPublicCover(resolvedForm)) {
            void navigate({ to: "/forms/$formId", params: { formId } });
            return;
        }
        goBackOrFallback(
            () => router.history.back(),
            () => {
                if (props.diaryId) {
                    void navigate({ to: "/diaries/$diaryId/home/to-do", params: { diaryId: props.diaryId } });
                } else {
                    void navigate({ to: "/" });
                }
            }
        );
    };
    const isSubmitting = props.isSubmitting ?? props.reactHookForm.formState.isSubmitting;

    if (showIntro && resolvedForm?.form_instructions) {
        return (
            <AppPageLayout
                padding="none"
                className={cn(
                    "flex flex-col notranslate",
                    props.constrain
                        ? "h-full min-h-0"
                        : isAppRoute
                          ? formPageShellFillClass
                          : publicFormColumnHeightClass
                )}
                as="div"
                translate="no"
            >
                <div className="flex flex-col flex-1 min-h-0" data-form-text>
                    <FormIntroScreen
                        instructions={resolvedForm.form_instructions}
                        startLabel={copy.next}
                        backLabel={copy.back}
                        onStart={() => setIntroStarted(introKey, true)}
                        onBack={handleBack}
                    />
                </div>
            </AppPageLayout>
        );
    }
    const pageInputs = Array.isArray(page?.inputs) ? (page.inputs as InputT[]) : [];
    const pageBlocks = Array.isArray(page?.blocks) ? (page.blocks as BlockT[]) : [];
    const pageValidation = parsePageValidation(page?.page_validation);
    const imageCountsRule = findImageCountsRule(pageValidation);
    const imageCounts = pairImageCounts(imageCountsRule, pageInputs, pageBlocks);

    const pageItems = [
        ...pageBlocks.map<PageRenderableItem>((block) => ({
            kind: "block",
            index: Number((block as BlockWithIndex)?.block_index || 0),
            key: `block-${block.block_id}`,
            block,
        })),
        ...pageInputs
            .filter((input) => !visibility.hidden.has(input.input_id))
            .map<PageRenderableItem>((input) => ({
                kind: "input",
                index: Number((input as InputWithIndex)?.input_index || 0),
                key: `input-${input.input_id}`,
                input,
            })),
    ].toSorted((a, b) => {
        if (a.index !== b.index) return a.index - b.index;
        if (a.kind === b.kind) return a.key.localeCompare(b.key);
        // Tie-breaker: show display blocks before answer inputs at same index.
        return a.kind === "block" ? -1 : 1;
    });

    const inlineInstructions = instructionPlacement === "page" ? trimmedField(resolvedForm?.form_instructions) : null;

    const pageColumn = (
        <div
            data-form-text
            className={
                props.constrain ? "flex flex-col min-h-full" : isAppRoute ? formPageShellFillClass : formPageColumnClass
            }
        >
            {laterality}
            {scheduleLabel ? <EntryForScheduleLine label={scheduleLabel} /> : null}
            {isEditingAnswers ? (
                <p
                    className="bg-slate-100 dark:bg-slate-800 mb-3 px-3 py-2 rounded-md text-slate-700 dark:text-slate-200 text-lg"
                    data-testid="form-edit-banner"
                    role="status"
                >
                    {copy.changingAnswers}
                </p>
            ) : null}
            {heading}

            <div className="shrink-0">
                <FormProgress
                    form={form ?? props.form}
                    pageIndex={props.progressPageIndex ?? props.pageIndex}
                    pageTotalOverride={props.progressPageTotal}
                />
            </div>

            {showPageLabel || pageDescription ? (
                <header data-anno="page-header" className="space-y-1 mt-4 text-left shrink-0">
                    {showPageLabel && pageLabel ? (
                        hasInstrumentTitle ? (
                            <h2
                                data-anno="page-label"
                                className="font-semibold text-slate-900 dark:text-slate-100 text-lg tracking-tight"
                            >
                                {pageLabel}
                            </h2>
                        ) : (
                            <cc.Title align="left" className="mb-0 md:mb-0 text-lg md:text-lg" words={pageLabel} />
                        )
                    ) : null}
                    {pageDescription ? (
                        <p
                            data-anno="page-description"
                            className="text-slate-600 dark:text-slate-400 text-lg text-pretty leading-snug"
                        >
                            <RichLabel>{pageDescription}</RichLabel>
                        </p>
                    ) : null}
                </header>
            ) : null}

            {inlineInstructions ? (
                <p
                    data-anno="instructions"
                    className="mt-2 mb-6 font-medium text-slate-700 dark:text-slate-400 text-lg text-left italic leading-snug tracking-wide shrink-0"
                >
                    <RichLabel>{inlineInstructions}</RichLabel>
                </p>
            ) : null}

            <ui.Form {...props.reactHookForm}>
                <form
                    noValidate
                    onSubmit={props.reactHookForm?.handleSubmit(props.onSubmit)}
                    className={`flex flex-col flex-1 p-0 text-lg ${pageInputs.length === 0 ? "mt-1" : "mt-3"}`}
                >
                    <div className="flex-1 space-y-4" ref={fieldsRef} data-anno="page-body">
                        {errorCount > 0 ? (
                            <div
                                role="alert"
                                className="bg-rose-50 dark:bg-rose-950/30 px-3 py-2.5 border border-rose-200 dark:border-rose-800/70 rounded-lg text-rose-900 dark:text-rose-100 text-lg"
                            >
                                <div className="flex items-center gap-2 font-medium">
                                    <AlertCircle
                                        size={16}
                                        className="text-rose-600 dark:text-rose-300 shrink-0"
                                        aria-hidden
                                    />
                                    {copy.errorsOnPage(errorCount)}
                                </div>
                                <ul className="space-y-1 mt-2 pt-2 border-rose-200/80 dark:border-rose-800/60 border-t text-lg text-left leading-snug list-none">
                                    {Object.entries(props.reactHookForm.formState.errors).map(([fieldId, err]) => {
                                        const input = pageInputs.find((i) => i.input_id === fieldId);
                                        const label = input?.input_label ?? fieldId;
                                        return (
                                            <li key={fieldId}>
                                                <span className="font-medium text-slate-800 dark:text-slate-100">
                                                    {label}
                                                </span>
                                                <span className="text-rose-800 dark:text-rose-200">
                                                    {" "}
                                                    - {String(err?.message ?? copy.invalidValue)}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ) : null}

                        {pageInputs.length === 0 ? (
                            <div className="space-y-3 text-left" data-testid="form-section-block">
                                {pageBlocks.map((block) =>
                                    block.block_type === "text" && block.content && !block.text_style ? (
                                        <div
                                            key={block.block_id}
                                            data-anno="block-text"
                                            className={cn(
                                                "text-slate-700 dark:text-slate-300 text-lg leading-relaxed",
                                                block.text_align === "center" && "text-center",
                                                block.text_align === "right" && "text-right"
                                            )}
                                        >
                                            <RichLabel>{block.content}</RichLabel>
                                        </div>
                                    ) : (
                                        <BlockSwitch key={block.block_id} block={block} />
                                    )
                                )}
                            </div>
                        ) : imageCounts && imageCountsRule ? (
                            <>
                                {pageItems.map((item) =>
                                    item.kind === "block" && item.block.block_id !== imageCounts.carouselBlockId ? (
                                        <div className="mb-4" key={item.key}>
                                            <BlockSwitch block={item.block} />
                                        </div>
                                    ) : null
                                )}
                                <ImageCountsGroup
                                    rule={imageCountsRule}
                                    rows={imageCounts.rows}
                                    control={props.reactHookForm.control}
                                    setFieldValue={(inputId, value) =>
                                        props.reactHookForm.setValue(inputId, value, { shouldDirty: true })
                                    }
                                />
                            </>
                        ) : (
                            groupCheckboxRuns<InputT, PageRenderableItem>(pageItems).map((item) =>
                                item.kind === "checkbox-group" ? (
                                    <div className="mb-4" key={item.key}>
                                        <CheckboxGroupCard
                                            inputs={item.items.map((checkbox) => checkbox.input)}
                                            control={props.reactHookForm.control}
                                            pageValidation={pageValidation}
                                        />
                                    </div>
                                ) : item.kind === "input" ? (
                                    <div className="mb-4" key={item.key} data-input-id={item.input.input_id}>
                                        <LabelInputScope inputId={item.input.input_id}>
                                            <InputSwitch
                                                form={props.reactHookForm}
                                                input={restrictChoices(
                                                    item.input,
                                                    visibility.allowedCodes.get(item.input.input_id),
                                                    ownOptions.filter((option) =>
                                                        visibility.allowedOwn
                                                            .get(item.input.input_id)
                                                            ?.includes(option.option_id)
                                                    )
                                                )}
                                                msAdlSelectionInputId={props.msAdlSelectionInputId}
                                            />
                                            {(item.input as CarriedInputT).carried_value != null ? (
                                                <p
                                                    className="mt-1 text-slate-600 dark:text-slate-400 text-lg"
                                                    data-testid="carried-forward-note"
                                                >
                                                    {copy.carriedNote}
                                                </p>
                                            ) : null}
                                            <PageGroupTotal
                                                validation={pageValidation}
                                                inputId={item.input.input_id}
                                                control={props.reactHookForm.control}
                                            />
                                        </LabelInputScope>
                                    </div>
                                ) : (
                                    <div className="mb-4" key={item.key}>
                                        <BlockSwitch block={item.block} />
                                    </div>
                                )
                            )
                        )}
                    </div>

                    <div className={formPageFooterFrameClass}>
                        <div className={formPageFooterActionsClass}>
                            <div className="footer-actions-primary">
                                {isLastPage ? (
                                    <fc.SubmitButton
                                        className="px-4"
                                        disabled={isSubmitting}
                                        isLoading={isSubmitting}
                                        data-testid="footer-primary-button"
                                    />
                                ) : (
                                    <fc.NextButton
                                        className="px-4"
                                        disabled={isSubmitting}
                                        isLoading={isSubmitting}
                                        data-testid="footer-primary-button"
                                    />
                                )}
                            </div>
                            {showBack && (
                                <div className="footer-actions-secondary">
                                    <fc.BackButton
                                        className="px-4 w-full whitespace-nowrap"
                                        label={copy.back}
                                        disabled={isSubmitting}
                                        onClick={handleBack}
                                        data-testid="footer-secondary-button"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </form>
            </ui.Form>
        </div>
    );

    return (
        <FormDisplayProvider hideRequiredMarker={!display.showRequiredMarker}>
            <AppPageLayout
                padding="none"
                className={cn("notranslate", props.constrain ? "h-full min-h-0" : isAppRoute && formPageShellFillClass)}
                as="div"
                translate="no"
            >
                {props.constrain ? <div className="h-full overflow-y-auto">{pageColumn}</div> : pageColumn}
            </AppPageLayout>
        </FormDisplayProvider>
    );
};

export default FormPage;
